import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchTool, adminUpdateResource, adminDeleteResource,
  adminResourceVersions, adminRollback, fetchResourceTypes, getAdminToken,
} from '../../api/client';

const FIELDS_TEXT = [
  { key: 'name', label: 'Name' },
  { key: 'website_url', label: 'Website URL' },
  { key: 'affiliate_url', label: 'Affiliate URL' },
  { key: 'logo', label: 'Logo URL' },
  { key: 'source', label: 'Source / attribution' },
  { key: 'short_description', label: 'Short description' },
  { key: 'primary_category', label: 'Primary category' },
  { key: 'subcategory', label: 'Subcategory' },
];

const BADGE_OPTIONS = [
  'verified', 'editors_pick', 'trending_tiktok', 'trending_youtube',
  'hidden_gem', 'weird_ai', 'beginner_friendly', 'rising_creator',
  'new', 'popular', 'sponsored', 'affiliate', 'premium', 'open_source',
];

export default function AdminToolEdit() {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [form, setForm] = useState(null);
  const [types, setTypes] = useState([]);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) {
      nav('/admin/login');
      return;
    }
    fetchResourceTypes().then(setTypes).catch(() => setTypes([]));
    reload();
  }, [slug, nav]);

  const reload = () => {
    fetchTool(slug).then((t) => {
      setTool(t);
      setForm({
        ...t,
        tags_str: (t.tags || []).join(', '),
        platform_str: (t.platform || []).join(', '),
        additional_categories_str: (t.additional_categories || []).join(', '),
        related_slugs_str: (t.related_slugs || []).join(', '),
        attributes_str: JSON.stringify(t.attributes || {}, null, 2),
      });
    });
    adminResourceVersions(slug).then(setVersions).catch(() => setVersions([]));
  };

  if (!form) return <div className="page-container"><div className="empty-state">Loading…</div></div>;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleBadge = (b) => {
    const cur = new Set(form.badges || []);
    if (cur.has(b)) cur.delete(b); else cur.add(b);
    update('badges', Array.from(cur));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    let attrs = {};
    try { attrs = JSON.parse(form.attributes_str || '{}'); }
    catch { setMsg('Attributes JSON is invalid'); setSaving(false); return; }
    const patch = {
      name: form.name,
      website_url: form.website_url,
      affiliate_url: form.affiliate_url || null,
      logo: form.logo || null,
      source: form.source || null,
      short_description: form.short_description || null,
      full_description: form.full_description || null,
      primary_category: form.primary_category,
      subcategory: form.subcategory || null,
      resource_type: form.resource_type,
      pricing_type: form.pricing_type,
      free_trial: !!form.free_trial,
      featured: !!form.featured,
      sponsored: !!form.sponsored,
      verified: !!form.verified,
      status: form.status,
      badges: form.badges || [],
      tags: form.tags_str.split(',').map((s) => s.trim()).filter(Boolean),
      platform: form.platform_str.split(',').map((s) => s.trim()).filter(Boolean),
      additional_categories: form.additional_categories_str.split(',').map((s) => s.trim()).filter(Boolean),
      related_slugs: form.related_slugs_str.split(',').map((s) => s.trim()).filter(Boolean),
      attributes: attrs,
    };
    try {
      const updated = await adminUpdateResource(slug, patch);
      setTool(updated);
      setMsg(`Saved. Now at v${updated.version}.`);
      reload();
    } catch (err) {
      setMsg('Save failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const doRollback = async (version) => {
    if (!window.confirm(`Rollback to v${version}? Current state will be saved as a new version first.`)) return;
    try {
      await adminRollback(slug, version);
      setMsg(`Rolled back to v${version}.`);
      reload();
    } catch (err) {
      setMsg('Rollback failed: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${tool.name}"? This cannot be undone (except from a version snapshot).`)) return;
    await adminDeleteResource(slug);
    nav('/admin/tools');
  };

  return (
    <div className="page-container admin-page">
      <div className="crumbs">
        <Link to="/admin/analytics">Admin</Link>
        <span>/</span>
        <Link to="/admin/tools">Resources</Link>
        <span>/</span>
        <span>{tool.name}</span>
      </div>
      <h1 className="page-title">Edit: {tool.name} <span className="muted" style={{ fontSize: 13 }}>· v{tool.version || 1}</span></h1>
      <form className="admin-edit-form" onSubmit={save} data-testid="admin-edit-form">
        <div className="field-row">
          <label className="field">
            <span>Resource type</span>
            <select value={form.resource_type} onChange={(e) => update('resource_type', e.target.value)} data-testid="edit-resource-type">
              {types.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
          </label>
        </div>
        {FIELDS_TEXT.map((f) => (
          <label className="field" key={f.key}>
            <span>{f.label}</span>
            <input
              type="text"
              value={form[f.key] || ''}
              onChange={(e) => update(f.key, e.target.value)}
              data-testid={`edit-${f.key}`}
            />
          </label>
        ))}
        <label className="field">
          <span>Full description</span>
          <textarea
            rows={5}
            value={form.full_description || ''}
            onChange={(e) => update('full_description', e.target.value)}
          />
        </label>
        <label className="field">
          <span>Additional categories (comma separated)</span>
          <input value={form.additional_categories_str} onChange={(e) => update('additional_categories_str', e.target.value)} />
        </label>
        <label className="field">
          <span>Tags (comma separated)</span>
          <input value={form.tags_str} onChange={(e) => update('tags_str', e.target.value)} />
        </label>
        <label className="field">
          <span>Platform (comma separated)</span>
          <input value={form.platform_str} onChange={(e) => update('platform_str', e.target.value)} />
        </label>
        <label className="field">
          <span>Related resource slugs (comma separated)</span>
          <input value={form.related_slugs_str} onChange={(e) => update('related_slugs_str', e.target.value)} />
        </label>
        <label className="field">
          <span>Attributes (JSON — flexible per-type data e.g. socials for creators)</span>
          <textarea rows={5} value={form.attributes_str} onChange={(e) => update('attributes_str', e.target.value)} />
        </label>
        <div className="field">
          <span>Badges</span>
          <div className="badge-picker">
            {BADGE_OPTIONS.map((b) => (
              <button
                type="button"
                key={b}
                className={`badge-toggle ${form.badges?.includes(b) ? 'active' : ''}`}
                onClick={() => toggleBadge(b)}
                data-testid={`badge-${b}`}
              >
                {b.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
        <div className="field-row">
          <label className="field">
            <span>Pricing</span>
            <select value={form.pricing_type} onChange={(e) => update('pricing_type', e.target.value)}>
              <option value="unknown">Unknown</option>
              <option value="free">Free</option>
              <option value="freemium">Freemium</option>
              <option value="paid">Paid</option>
              <option value="open-source">Open source</option>
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select value={form.status} onChange={(e) => update('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="waitlist">Waitlist</option>
            </select>
          </label>
        </div>
        <div className="checkbox-row">
          <label><input type="checkbox" checked={!!form.featured} onChange={(e) => update('featured', e.target.checked)} /> Featured</label>
          <label><input type="checkbox" checked={!!form.sponsored} onChange={(e) => update('sponsored', e.target.checked)} /> Sponsored</label>
          <label><input type="checkbox" checked={!!form.verified} onChange={(e) => update('verified', e.target.checked)} /> Verified</label>
          <label><input type="checkbox" checked={!!form.free_trial} onChange={(e) => update('free_trial', e.target.checked)} /> Free trial</label>
        </div>
        {msg && <div className={msg.startsWith('Saved') || msg.startsWith('Rolled') ? 'success-msg' : 'error-msg'}>{msg}</div>}
        <div className="form-actions">
          <Link to="/admin/tools" className="pill-btn">Back</Link>
          <button type="button" className="pill-btn danger" onClick={doDelete}>Delete</button>
          <button type="submit" className="pill-btn primary" disabled={saving} data-testid="admin-save-btn">
            {saving ? '…' : 'Save'}
          </button>
        </div>
      </form>

      {versions.length > 0 && (
        <section className="admin-versions" data-testid="admin-versions">
          <h3>Version history</h3>
          <table className="admin-table">
            <thead><tr><th>Version</th><th>By</th><th>When</th><th>Note</th><th></th></tr></thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version}>
                  <td>v{v.version}</td>
                  <td>{v.changed_by}</td>
                  <td>{new Date(v.changed_at).toLocaleString()}</td>
                  <td>{v.change_note || '—'}</td>
                  <td><button type="button" className="pill-btn ghost sm" onClick={() => doRollback(v.version)}>Rollback</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
